import { useEffect, useState, useCallback } from 'react';
import { RefreshCw, ServerCrash, Cpu, HardDrive, Activity } from 'lucide-react';
import { apiService, type WatchdogReportSummary, type WatchdogReportDoc } from '@/services/api.service';
import { ServerTabs } from '@/components/ServerTabs';
import { GameGroupBadge } from '@/components/ui/GameGroupBadge';
import { useActiveGameGroup } from '@/stores/gameGroups.store';
import styles from './WatchdogPage.module.scss';

const EVENT_META: Record<string, { label: string; color: string }> = {
  Freeze: { label: 'Freeze', color: '#E74C3C' },
  Restart: { label: 'Redémarrage', color: '#E74C3C' },
  Degraded: { label: 'Dégradé', color: '#E67E22' },
  HighMemory: { label: 'Mémoire haute', color: '#E67E22' },
  Recovery: { label: 'Rétabli', color: '#2ECC71' },
  Dump: { label: 'Dump', color: '#3498DB' },
};
function eventMeta(ev: string | null) {
  return (ev && EVENT_META[ev]) || { label: ev || 'Incident', color: '#95A5A6' };
}
function stateColor(state: string | null | undefined): string {
  if (state === 'Frozen') return '#E74C3C';
  if (state === 'Degraded') return '#E67E22';
  if (state === 'Healthy') return '#2ECC71';
  return '#95A5A6';
}
function fmtDate(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeStyle: 'medium' }).format(d);
}
function fmtBytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  const gb = n / 1073741824;
  if (gb >= 1) return `${gb.toFixed(1)} Go`;
  return `${(n / 1048576).toFixed(0)} Mo`;
}
function num(n: number | null | undefined, digits = 1): string {
  return n === null || n === undefined ? '—' : n.toFixed(digits);
}

export function WatchdogPage() {
  // Groupe de jeu consulté (NationsGlory) : ses rapports vivent dans la base du satellite du groupe.
  const activeGroup = useActiveGameGroup();
  const [reports, setReports] = useState<WatchdogReportSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<WatchdogReportDoc | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await apiService.getWatchdogReports();
    if (res.error) setError(res.error);
    else {
      const list = res.data?.reports ?? [];
      setReports(list);
      setSelectedId((cur) => cur ?? (list[0]?.id ?? null));
    }
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    let cancelled = false;
    setDetailLoading(true);
    void apiService.getWatchdogReport(selectedId).then((res) => {
      if (cancelled) return;
      setDetail(res.data ?? null);
      setDetailLoading(false);
    });
    return () => { cancelled = true; };
  }, [selectedId]);

  return (
    <div className={styles.page}>
      <ServerTabs />
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Crashs serveur <GameGroupBadge size="md" /></h1>
          <p className={styles.subtitle}>
            Freezes, dégradations TPS et alertes mémoire détectés par le Watchdog.
          </p>
        </div>
        <button className={styles.refresh} onClick={() => void load()} disabled={loading}>
          <RefreshCw size={16} /> Rafraîchir
        </button>
      </header>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.empty}>Chargement…</div>
      ) : reports.length === 0 && !error ? (
        <div className={styles.empty}>
          <ServerCrash size={28} />
          <p>Aucun incident remonté.</p>
          <span>
            {activeGroup
              ? `Les rapports du Watchdog apparaîtront ici une fois le mod câblé sur les serveurs ${activeGroup.label} (webhook vers le satellite ${activeGroup.label}).`
              : 'Les rapports du Watchdog apparaîtront ici une fois le mod câblé sur ce projet.'}
          </span>
        </div>
      ) : (
        <div className={styles.split}>
          <aside className={styles.list}>
            {reports.map((r) => {
              const meta = eventMeta(r.event);
              return (
                <button
                  key={r.id}
                  className={`${styles.item} ${selectedId === r.id ? styles.itemActive : ''}`}
                  onClick={() => setSelectedId(r.id)}
                >
                  <div className={styles.itemTop}>
                    <span className={styles.badge} style={{ background: meta.color + '22', color: meta.color, borderColor: meta.color + '55' }}>
                      {meta.label}
                    </span>
                    <span className={styles.itemServer}>{r.serverName ?? '—'}</span>
                  </div>
                  <div className={styles.itemMeta}>
                    <span style={{ color: stateColor(r.worstLevelState) }}>
                      {r.worstLevelName ?? '—'} · {r.worstLevelState ?? '—'}
                    </span>
                    <span>TPS {num(r.minTps)}</span>
                    {r.memoryPercent != null && <span>Mém {num(r.memoryPercent, 0)}%</span>}
                  </div>
                  <div className={styles.itemDate}>{fmtDate(r.receivedAt)}</div>
                </button>
              );
            })}
          </aside>

          <section className={styles.detail}>
            {detailLoading ? (
              <div className={styles.empty}>Chargement du rapport…</div>
            ) : !detail ? (
              <div className={styles.empty}>Sélectionne un incident.</div>
            ) : (
              <ReportDetail doc={detail} />
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function ReportDetail({ doc }: { doc: WatchdogReportDoc }) {
  const meta = eventMeta(doc.event);
  const r = doc.report;
  return (
    <article className={styles.card}>
      <header className={styles.detailHead}>
        <span className={styles.badge} style={{ background: meta.color + '22', color: meta.color, borderColor: meta.color + '55' }}>
          {meta.label}
        </span>
        <div className={styles.detailTitle}>
          <h2>{r?.ServerName ?? doc.serverName ?? 'Serveur'}</h2>
          <span className={styles.detailSub}>
            {doc.trigger ?? r?.Trigger} · {fmtDate(r?.TimestampUtc ?? doc.timestampUtc ?? doc.receivedAt)}
          </span>
        </div>
      </header>

      {/* Levels */}
      {r?.Levels?.length ? (
        <section className={styles.section}>
          <h3><Activity size={15} /> Levels</h3>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr><th>Level</th><th>État</th><th>TPS</th><th>MSPT</th><th>Async TPS</th><th>Figé (s)</th></tr>
              </thead>
              <tbody>
                {r.Levels.map((l) => (
                  <tr key={l.Name}>
                    <td>{l.Name}</td>
                    <td><span style={{ color: stateColor(l.State) }}>{l.State}</span></td>
                    <td>{num(l.Tps)}</td>
                    <td>{num(l.Mspt)}</td>
                    <td>{num(l.AsyncTps)}</td>
                    <td>{num(l.SecondsFrozen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {r.Levels.map((l) => l.TickDiagnostics ? (
            <div key={l.Name} className={styles.diag}><strong>{l.Name}</strong> · {l.TickDiagnostics}</div>
          ) : null)}
        </section>
      ) : null}

      {/* Analyse des verrous */}
      {r?.LockAnalysis?.length ? (
        <section className={styles.section}>
          <h3>Analyse des verrous</h3>
          <ul className={styles.locks}>
            {r.LockAnalysis.map((line, i) => <li key={i}>{line}</li>)}
          </ul>
        </section>
      ) : null}

      {/* Mémoire / GC / ThreadPool */}
      <section className={styles.cards}>
        {r?.Memory && (
          <div className={styles.metric}>
            <span className={styles.metricIcon}><HardDrive size={16} /></span>
            <div><span className={styles.metricLabel}>Mémoire</span>
              <span className={styles.metricValue}>{num(r.Memory.Percent, 0)}%</span>
              <span className={styles.metricHint}>{fmtBytes(r.Memory.UsedBytes)} / {fmtBytes(r.Memory.LimitBytes)}</span>
            </div>
          </div>
        )}
        {r?.Gc && (
          <div className={styles.metric}>
            <span className={styles.metricIcon}><Cpu size={16} /></span>
            <div><span className={styles.metricLabel}>GC</span>
              <span className={styles.metricValue}>{fmtBytes(r.Gc.TotalMemoryBytes)}</span>
              <span className={styles.metricHint}>gen0 {r.Gc.Gen0Collections} · gen1 {r.Gc.Gen1Collections} · gen2 {r.Gc.Gen2Collections}</span>
            </div>
          </div>
        )}
        {r?.ThreadPool && (
          <div className={styles.metric}>
            <span className={styles.metricIcon}><Cpu size={16} /></span>
            <div><span className={styles.metricLabel}>ThreadPool</span>
              <span className={styles.metricValue}>{r.ThreadPool.ThreadCount} threads</span>
              <span className={styles.metricHint}>en attente {r.ThreadPool.PendingWorkItems}</span>
            </div>
          </div>
        )}
      </section>

      {/* Threads */}
      {r?.Threads?.length ? (
        <section className={styles.section}>
          <h3><Cpu size={15} /> Threads ({r.Threads.length})</h3>
          <div className={styles.threads}>
            {r.Threads.map((t) => (
              <details key={t.ManagedThreadId} className={`${styles.thread} ${t.IsLevelThread ? styles.threadSuspect : ''}`}>
                <summary>
                  <span className={styles.threadId}>#{t.ManagedThreadId}</span>
                  {t.IsLevelThread && <span className={styles.suspect}>thread de level</span>}
                  {t.IsGc && <span className={styles.gcTag}>GC</span>}
                  {t.LockNotes?.length ? <span className={styles.lockNote}>{t.LockNotes?.[0]}</span> : null}
                </summary>
                <pre className={styles.frames}>{(t.Frames ?? []).join('\n')}</pre>
              </details>
            ))}
          </div>
        </section>
      ) : null}
    </article>
  );
}
